import i18n from "i18next";
import { useMemo, type HTMLAttributes } from "react";
import { noteSymbol, parseTitle, parseTitleAuthors, plainText } from "@/lib/titleAuthors";
import styles from "./blocks.module.scss";

interface TitleBlockViewProps {
  title: string;
  author: string;
  /** The date as it prints. */
  dateText: string;
  dateHint?: string;
  /**
   * The modal's paper. Drops the canvas testids (the canvas is still on the
   * page behind the modal) and sets the footnotes at the foot of the page.
   */
  paper?: boolean;
  /** A note to light up, by its symbol (hovering a note in the form). */
  highlightSymbol?: string | null;
  /** False where nobody can edit (a reader, a phone): no "Author" or "Document title" stand-ins. */
  showPlaceholders?: boolean;
  /** Replaces the authors when they cannot be laid out (LaTeX we keep as written). */
  authorsNotice?: string;
  wrapperProps?: HTMLAttributes<HTMLDivElement>;
  /** A copy shown elsewhere on the page (the LaTeX view's Blocks tab): the canvas keeps the testids. */
  withoutTestIds?: boolean;
}

/**
 * The title block as it prints: title, authors in columns with symbols, date,
 * notes. The canvas and the modal's paper are both this, fed by the same
 * parse of the stored strings.
 */
export function TitleBlockView({
  title,
  author,
  dateText,
  dateHint,
  paper = false,
  highlightSymbol,
  authorsNotice,
  wrapperProps,
  showPlaceholders = true,
  withoutTestIds = false,
}: TitleBlockViewProps) {
  const tid = (id: string) => (paper || withoutTestIds ? undefined : id);
  const parsedTitle = useMemo(() => parseTitle(title), [title]);
  const titleNotes = parsedTitle.notes.map((text, i) => ({ text: plainText(text), symbol: noteSymbol(i) }));
  // Notes are counted across the title block: the title's own come first.
  const authors = useMemo(() => parseTitleAuthors(author, parsedTitle.notes.length), [author, parsedTitle.notes.length]);
  const notes = [
    ...titleNotes,
    ...authors.flatMap((a) => a.notes.map((text, i) => ({ text, symbol: a.symbols[i] }))),
  ];
  // Several authors, or any footnote, get the laid-out block; one plain
  // author keeps printing the stored text exactly as before.
  const structured = authors.length > 1 || notes.length > 0 || authors.some((a) => a.lines.length > 0);

  const noteList = notes.length > 0 && (
    <div className={paper ? styles.titleFootnotes : styles.titleNotes}>
      {notes.map((n, i) => (
        <div
          key={i}
          className={styles.titleNote}
          data-testid={tid("title-block-note")}
          data-symbol={n.symbol}
          data-highlight={n.symbol === highlightSymbol ? "true" : undefined}
        >
          <span className={styles.titleNoteSymbol}>{n.symbol}</span> {n.text}
        </div>
      ))}
    </div>
  );

  return (
    <div className={styles.titleBlock} {...wrapperProps}>
      <div
        className={styles.titleMain}
        data-testid={tid("title-block-title")}
        data-placeholder="Document title"
      >
        {parsedTitle.text ? (
          <>
            {parsedTitle.text}
            {titleNotes.length > 0 && (
              <sup className={styles.titleNoteSymbol}>{titleNotes.map((n) => n.symbol).join(",")}</sup>
            )}
          </>
        ) : (
          showPlaceholders ? <span style={{ color: "var(--lilia-text-muted)", fontWeight: 400 }}>{i18n.t("editor:titleBlock.titlePlaceholder")}</span> : null
        )}
      </div>
      <div className={styles.titleAuthor} data-testid={tid("title-block-author")}>
        {authorsNotice ? (
          <span style={{ color: "var(--lilia-text-muted)" }}>{authorsNotice}</span>
        ) : !author ? (
          showPlaceholders ? <span style={{ color: "var(--lilia-text-muted)" }}>{i18n.t("editor:titleBlock.authorPlaceholder")}</span> : null
        ) : structured ? (
          <div className={styles.titleAuthors}>
            {authors.map((a, i) => (
              <div
                key={i}
                className={styles.titleAuthorItem}
                data-testid={tid("title-block-author-item")}
                data-index={i}
              >
                <div className={styles.titleAuthorName}>
                  {a.name}
                  {a.symbols.length > 0 && (
                    <sup className={styles.titleNoteSymbol}>{a.symbols.join(",")}</sup>
                  )}
                </div>
                {a.lines.map((line, j) => (
                  <div key={j} className={styles.titleAuthorLine}>
                    {line}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          author
        )}
      </div>
      <div className={styles.titleDate} data-testid={tid("title-block-date")} title={dateHint}>
        {dateText}
      </div>
      {noteList}
    </div>
  );
}
